import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { ChevronDown, Hash, Plus, Smile } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
// ---------------------------------------------------------------------------
// Data — a set of Slack-style "conversations" that each end with the team
// @mentioning Giga and Giga replying with a finished result. The hero cycles
// through them so visitors see several use cases, the way Viktor's reel does.
// ---------------------------------------------------------------------------
type ResultCard = {
  title: string;
  meta: string;
  stat: string;
  statLabel: string;
  bars: number[];
  fileName: string;
  fileMeta: string;
};
type Msg = {
  author: string;
  avatar: string;
  time: string;
  app?: boolean;
  mention?: string;
  text: string;
  card?: ResultCard;
  reactions?: {
    emoji: string;
    count: number;
  }[];
};
type Scenario = {
  channel: string;
  messages: Msg[];
};
const SCENARIOS: Scenario[] = [{
  channel: 'results',
  messages: [{
    author: 'Maya Chen',
    avatar: 'https://i.pravatar.cc/80?img=47',
    time: '8:01 AM',
    mention: '@Giga',
    text: ' pull the weekly numbers from Stripe, GA4, and HubSpot and post the recap.'
  }, {
    author: 'Jordan Ellis',
    avatar: 'https://i.pravatar.cc/80?img=12',
    time: '8:02 AM',
    text: 'flag anything that moved more than 10% week over week'
  }, {
    author: 'Giga',
    avatar: '',
    time: '8:06 AM',
    app: true,
    text: 'Done. Revenue, signups, CAC, and pipeline in one view. Paid signups up 23%, refunds down. Want me to run this every Monday at 8?',
    card: {
      title: 'Weekly performance',
      meta: 'Northwind · week of Jun 8',
      stat: '+23%',
      statLabel: 'paid signups w/w',
      bars: [40, 55, 38, 70, 48, 82, 60, 95, 72],
      fileName: 'Weekly-Performance.pdf',
      fileMeta: '6 pages · PDF'
    },
    reactions: [{
      emoji: '🎉',
      count: 5
    }, {
      emoji: '💯',
      count: 2
    }]
  }]
}, {
  channel: 'support',
  messages: [{
    author: 'Priya Nair',
    avatar: 'https://i.pravatar.cc/80?img=32',
    time: '9:14 AM',
    mention: '@Giga',
    text: ' the Acme account is escalating — can you pull their last 5 tickets and draft a reply?'
  }, {
    author: 'Tomás Rivera',
    avatar: 'https://i.pravatar.cc/80?img=15',
    time: '9:15 AM',
    text: 'include the refund we promised them last week'
  }, {
    author: 'Giga',
    avatar: '',
    time: '9:17 AM',
    app: true,
    text: 'Pulled all 5 tickets from Zendesk and the refund from Stripe. Drafted a reply that references both and offers a 2-month credit. Send it?',
    card: {
      title: 'Acme · escalation',
      meta: '5 tickets · 1 refund',
      stat: '4.9★',
      statLabel: 'CSAT after fix',
      bars: [30, 45, 35, 60, 52, 75, 68, 88, 80],
      fileName: 'Acme-Reply-Draft.txt',
      fileMeta: 'Ready to send'
    },
    reactions: [{
      emoji: '🙏',
      count: 3
    }, {
      emoji: '🔥',
      count: 4
    }]
  }]
}, {
  channel: 'growth',
  messages: [{
    author: 'Sofia Alvarez',
    avatar: 'https://i.pravatar.cc/80?img=5',
    time: '11:02 AM',
    mention: '@Giga',
    text: ' compare our top 7 paid suppliers and find where we’re overspending.'
  }, {
    author: 'Liam O’Brien',
    avatar: 'https://i.pravatar.cc/80?img=68',
    time: '11:03 AM',
    text: 'group it by channel so we can cut the worst one'
  }, {
    author: 'Giga',
    avatar: '',
    time: '11:06 AM',
    app: true,
    text: 'Compared all 7. One channel is 41% above the median CPA with no lift. Swapping budget to the top 2 saves ~$18k/mo. Want the breakdown?',
    card: {
      title: 'Spend audit',
      meta: '7 channels · last 30 days',
      stat: '-$18k',
      statLabel: 'projected monthly save',
      bars: [88, 72, 60, 95, 40, 30, 55, 48, 35],
      fileName: 'Spend-Audit.pdf',
      fileMeta: '4 pages · PDF'
    },
    reactions: [{
      emoji: '🤑',
      count: 6
    }, {
      emoji: '👏',
      count: 3
    }]
  }]
}];
const CHANNELS = ['results', 'general', 'finance', 'eng', 'growth', 'random'];
// ---------------------------------------------------------------------------
function GigaAvatar() {
  return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#EA580C] shadow-sm" data-id="element-6508" data-mp="1rlkg4sb9euhp">
      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="Giga" className="h-4 w-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-6509" data-mp="1wkx27aub3xj8" />
    </span>;
}
function ResultCardView({
  card
}: {
  card: ResultCard;
}) {
  return <div className="mt-2 w-full max-w-[320px] overflow-hidden rounded-2xl border border-[#F1E6DA] bg-white shadow-[0_8px_24px_rgba(43,33,26,0.08)]" data-id="element-6510" data-mp="2ajd5sswvh7x5">
      {/* warm gradient header with chart */}
      <div className="relative bg-gradient-to-br from-[#FBE3C7] via-[#F8C98E] to-[#EA580C]/70 p-4" data-id="element-6511" data-mp="g6ne8htz8asx">
        <div className="flex items-start justify-between" data-id="element-6512" data-mp="8nxjnwftqkdo">
          <div data-id="element-6513" data-mp="3v4g3bw3di36">
            <p className="font-title text-lg font-semibold leading-tight text-[#2B211A]" data-id="element-6514" data-mp="3ul8op42ic3e">
              {card.title}
            </p>
            <p className="text-[11px] font-medium text-[#2B211A]/60" data-id="element-6515" data-mp="3v274xwogjca8">
              {card.meta}
            </p>
          </div>
          <div className="text-right" data-id="element-6516" data-mp="rl8hrdppz7rj">
            <p className="font-title text-2xl font-bold leading-none text-[#2B211A]" data-id="element-6517" data-mp="rk5rn80819co">
              {card.stat}
            </p>
            <p className="text-[10px] font-medium text-[#2B211A]/60" data-id="element-6518" data-mp="mmx7x2zfcx9b">
              {card.statLabel}
            </p>
          </div>
        </div>
        <div className="mt-4 flex h-12 items-end gap-1" data-id="element-6519" data-mp="dlpk5eujbeth">
          {card.bars.map((h, i) => <div key={i} className="flex-1 rounded-t-[3px] bg-white/55" style={{
          height: `${h}%`
        }} data-id="element-6520" data-mp="33arlucm407or" data-mpi={i} />)}
        </div>
      </div>
      {/* file pill */}
      <div className="flex items-center gap-3 px-4 py-3" data-id="element-6521" data-mp="2yadc8q4v0pom">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FAF3EC] text-[11px] font-bold text-[#EA580C]" data-id="element-6522" data-mp="2zske16uzsx4u">
          PDF
        </span>
        <div className="min-w-0" data-id="element-6523" data-mp="34pki5c8a7ptb">
          <p className="truncate text-sm font-semibold text-[#2B211A]" data-id="element-6524" data-mp="2qsx9ic6yvb7k">
            {card.fileName}
          </p>
          <p className="text-[11px] text-neutral-400" data-id="element-6525" data-mp="2usfvzcefruci">{card.fileMeta}</p>
        </div>
      </div>
    </div>;
}
function MessageRow({
  msg,
  index
}: {
  msg: Msg;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.45,
    ease: [0.22, 1, 0.36, 1],
    delay: index * 0.04
  }} className="flex gap-3 px-4 py-2.5 md:px-6" data-id="element-6526" data-mp="2lu48hj8v9coa">
      {msg.app ? <GigaAvatar data-id="element-6527" data-mp="2c0wu58w5emc2" /> : <img src={msg.avatar} alt={msg.author} className="h-9 w-9 shrink-0 rounded-[10px] object-cover" data-id="element-6528" data-mp="1y3hyhmfaxibn" />}
      <div className="min-w-0 flex-1" data-id="element-6529" data-mp="232bgns5j9xi5">
        <div className="flex items-center gap-2" data-id="element-6530" data-mp="2319sqn3f5gj0">
          <span className="text-sm font-bold text-[#2B211A]" data-id="element-6531" data-mp="1jgrrc4ewcvi5">{msg.author}</span>
          {msg.app && <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-neutral-500" data-id="element-6532" data-mp="1jim8r8qym5hh">
              APP
            </span>}
          <span className="text-[11px] text-neutral-400" data-id="element-6533" data-mp="1si91751po0ye">{msg.time}</span>
        </div>
        <p className="mt-0.5 text-sm leading-relaxed text-[#2B211A]" data-id="element-6534" data-mp="1oet5qc1do6na">
          {msg.mention && <span className="rounded bg-[#EA580C]/12 px-1 font-semibold text-[#C2410C]" data-id="element-6535" data-mp="22evmfuxy8u0y">
              {msg.mention}
            </span>}
          {msg.text}
        </p>
        {msg.card && <ResultCardView card={msg.card} data-id="element-6536" data-mp="2c5738i2qexp9" />}
        {msg.reactions && <div className="mt-2 flex items-center gap-1.5" data-id="element-6537" data-mp="2c4nhsrdvhr6h">
            {msg.reactions.map(r => <span key={r.emoji} className="flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs font-medium text-neutral-600" data-id="element-6538" data-mp="276vs840pah5g">
                <span data-id="element-6539" data-mp="2g7tn9wt5vebw">{r.emoji}</span>
                {r.count}
              </span>)}
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 text-neutral-400" data-id="element-6540" data-mp="3nk0w4azsiakm">
              <Smile size={13} data-id="element-6541" data-mp="3sh144rg79ned" />
            </span>
          </div>}
      </div>
    </motion.div>;
}
export function WorkspaceHero() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    margin: '-15%',
    once: false
  });
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [revealed, setRevealed] = useState(reduced ? 99 : 0);
  const scenario = SCENARIOS[scenarioIndex];
  const total = scenario.messages.length;
  // Reveal messages one-by-one; once the conversation is complete, hold, then
  // advance to the next scenario. Pauses entirely when off-screen.
  useEffect(() => {
    if (reduced) {
      setRevealed(total);
      return;
    }
    if (!inView) return;
    if (revealed < total) {
      // Giga's reply (the last message) gets a longer "thinking" beat.
      const isGigaNext = revealed === total - 1;
      const id = setTimeout(() => setRevealed(r => r + 1), isGigaNext ? 1100 : 750);
      return () => clearTimeout(id);
    }
    // Conversation complete — hold, then swap to the next scenario.
    const id = setTimeout(() => {
      setScenarioIndex(i => (i + 1) % SCENARIOS.length);
      setRevealed(0);
    }, 3200);
    return () => clearTimeout(id);
  }, [inView, revealed, total, scenarioIndex, reduced]);
  const shown = scenario.messages.slice(0, reduced ? total : revealed);
  return <section className="relative w-full overflow-hidden bg-[#FAF8F4] pt-28 pb-16 md:pt-36 md:pb-24" data-id="element-6542" data-mp="1uu8dwn4p4f9u">
      {/* soft warm wash */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(120% 70% at 50% 0%, rgba(234,88,12,0.07) 0%, rgba(250,248,244,0) 55%)'
    }} data-id="element-6543" data-mp="1ptuaxjhybvio" />

      <div className="relative z-10 mx-auto max-w-[1100px] px-5 md:px-8" data-id="element-6544" data-mp="1gx1ucqc5ecjk">
        {/* Marketing header */}
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16" data-id="element-6545" data-mp="2nglhh20ppxdm">
          <span className="mb-5 block text-xs font-semibold uppercase tracking-[5px] text-[#EA580C]" data-id="element-6546" data-mp="2nesatmq3nd5m">
            Works where you work
          </span>
          <h1 className="font-title text-4xl font-medium leading-[1.05] tracking-tight text-[#2B211A] sm:text-5xl md:text-6xl" data-id="element-6547" data-mp="2x6y1mt4m7oj8">
            A teammate in every{' '}
            <span className="italic font-light" data-id="element-6548" data-mp="2s9o38nz902j9">channel.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#6B5D52] md:text-lg" data-id="element-6549" data-mp="24hzdnfdt9s60">
            @mention Giga in Slack or Teams. It reads your tools, does the work,
            and drops the finished result right where the conversation is.
          </p>
        </div>

        {/* Workspace window */}
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto overflow-hidden rounded-[20px] border border-neutral-200/80 bg-white shadow-[0_30px_80px_-20px_rgba(43,33,26,0.25)]" data-id="element-6550" data-mp="29j6o5m7c95gd">
          {/* Top bar */}
          <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3 md:px-6" data-id="element-6551" data-mp="3jfvbjldhahuy">
            <div className="flex items-center gap-2" data-id="element-6552" data-mp="3nh6qmwyq7kow">
              <span className="font-title text-base font-bold text-[#2B211A]" data-id="element-6553" data-mp="f0ltcdfgig1m">
                Workspace
              </span>
              <ChevronDown size={15} className="text-neutral-400" data-id="element-6554" data-mp="jzyduriuvb11" />
            </div>
            {/* Slack / Teams toggle */}
            <div className="flex items-center gap-1 rounded-full bg-neutral-100 p-1" data-id="element-6555" data-mp="67s7fx5i8ies">
              <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-neutral-500" data-id="element-6556" data-mp="a8ki8uu0yvd6">
                <img src="https://cdn.simpleicons.org/microsoftteams" alt="" aria-hidden="true" className="h-3.5 w-3.5" data-id="element-6557" data-mp="xy6f3bmxcyi0" />
                <span className="hidden sm:inline" data-id="element-6558" data-mp="12s0z6g6fzfhm">Teams</span>
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#2B211A] shadow-sm" data-id="element-6559" data-mp="1ck2fmyoglqgt">
                <img src="https://cdn.simpleicons.org/slack" alt="" aria-hidden="true" className="h-3.5 w-3.5" data-id="element-6560" data-mp="13kyvrshgx719" />
                Slack
              </span>
            </div>
          </div>

          <div className="flex" data-id="element-6561" data-mp="tnaqi8m8kh4u">
            {/* Sidebar */}
            <aside className="hidden w-52 shrink-0 border-r border-neutral-100 bg-[#FCFAF7] p-4 md:block" data-id="element-6562" data-mp="tolqdxck94qo">
              <div className="mb-2 flex items-center gap-1 text-xs font-semibold text-neutral-500" data-id="element-6563" data-mp="ouhjfe3aowpl">
                <ChevronDown size={13} data-id="element-6564" data-mp="kswb861ph6lq" />
                Channels
              </div>
              <ul className="space-y-0.5" data-id="element-6565" data-mp="fxgwlbvvj6o9">
                {CHANNELS.map(c => {
                const active = c === scenario.channel;
                return <li key={c} data-id="element-6566" data-mp="5xzvl7rkhgym">
                      <span className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors ${active ? 'bg-[#EA580C]/10 font-semibold text-[#2B211A]' : 'text-neutral-500'}`} data-id="element-6567" data-mp="5yrilu73m6da">
                        <Hash size={13} className={active ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-6568" data-mp="run8igwty9v" />
                        {c}
                      </span>
                    </li>;
              })}
              </ul>
              {/* skeleton rows */}
              <div className="mt-4 space-y-2.5 pl-1" data-id="element-6569" data-mp="aopkyz5czun3">
                {[60, 80, 50, 70, 45].map((w, i) => <div key={i} className="flex items-center gap-2" data-id="element-6570" data-mp="396uw7u4gkg7q" data-mpi={i}>
                    <div className="h-2 w-2 rounded-sm bg-neutral-200" data-id="element-6571" data-mp="3e8ai1oegmqr6" />
                    <div className="h-2 rounded bg-neutral-200" style={{
                  width: `${w}%`
                }} data-id="element-6572" data-mp="3j6lqbup3wt7a" />
                  </div>)}
              </div>
            </aside>

            {/* Thread */}
            <div className="flex min-h-[440px] flex-1 flex-col" data-id="element-6573" data-mp="2qheu52d4kfp7">
              {/* channel header */}
              <div className="flex items-center gap-1.5 border-b border-neutral-100 px-4 py-3 md:px-6" data-id="element-6574" data-mp="2vfztz0ogsxd4">
                <Hash size={16} className="text-neutral-400" data-id="element-6575" data-mp="111m03du1y0cv" />
                <span className="font-title text-base font-bold text-[#2B211A]" data-id="element-6576" data-mp="w2hqim37u0m0">
                  {scenario.channel}
                </span>
                <ChevronDown size={14} className="text-neutral-400" data-id="element-6577" data-mp="1asgr0vi8to4n" />
              </div>

              {/* messages */}
              <div className="flex-1 py-3" data-id="element-6578" data-mp="15ycfs01y2txr">
                <AnimatePresence mode="wait" data-id="element-6579" data-mp="1jqsmpbxyvc3y">
                  <motion.div key={scenarioIndex} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-6580" data-mp="1jtn6d195oswb">
                    {shown.map((msg, i) => <MessageRow key={i} msg={msg} index={i} data-id="element-6581" data-mp="1oou29mw6otao" data-mpi={i} />)}
                    {/* typing indicator while Giga "thinks" */}
                    {!reduced && revealed === total - 1 && <div className="flex items-center gap-2 px-4 py-2 md:px-6" data-id="element-6582" data-mp="3qjl3b2g82e5q">
                        <GigaAvatar data-id="element-6583" data-mp="261ail5f2wg1x" />
                        <div className="flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-2" data-id="element-6584" data-mp="262bt98s6n0j4">
                          {[0, 1, 2].map(d => <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-neutral-400" animate={{
                        opacity: [0.3, 1, 0.3]
                      }} transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: d * 0.18
                      }} data-id="element-6585" data-mp="1wafs1ousuuao" />)}
                        </div>
                      </div>}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* composer */}
              <div className="border-t border-neutral-100 p-3 md:p-4" data-id="element-6586" data-mp="1i9lk17bnyvcj">
                <div className="flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2.5" data-id="element-6587" data-mp="37pbie82t6k72">
                  <Plus size={16} className="text-neutral-400" data-id="element-6588" data-mp="3co61lb4ouarp" />
                  <span className="flex-1 text-sm text-neutral-400" data-id="element-6589" data-mp="3cnmr2fhdaonp">
                    Message #{scenario.channel}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="mt-10 flex flex-col items-center gap-4 md:mt-12" data-id="element-6590" data-mp="318b524gy8vtb">
          <WaitlistCTA className="inline-flex items-center justify-center rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:scale-105 hover:bg-[#d24e09]" data-id="element-6591" data-mp="2x7r6cwgq9k52">
            Add Giga to your workspace
          </WaitlistCTA>
          <p className="text-xs text-neutral-400" data-id="element-6592" data-mp="3k7p24v9z6mo1">
            Live in Slack or Teams in two minutes — no setup project required.
          </p>
        </div>
      </div>
    </section>;
}